import { useCallback, useMemo } from 'react';

import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

/**
 * Content packs (feat/room-to-revenue, workstream C): for each product a room
 * shot, a packshot, a clip and a few posts, made automatically and waiting for
 * the vendor's yes. Mirrors pms-api-v2 `pms-content-packs`.
 */

export type ContentPackItemKind = 'room' | 'packshot' | 'clip' | 'post';
export type ContentPackStatus = 'queued' | 'generating' | 'ready' | 'partial' | 'failed';
export type ContentPackItemStatus = 'queued' | 'generating' | 'ready' | 'failed';
export type ContentPackApproval = 'pending' | 'approved' | 'rejected';
export type ContentPackFilter = 'waiting' | 'approved' | 'all';
export type ContentPackPushCode =
    | 'not_connected'
    | 'unsupported_platform'
    | 'no_publish_grant'
    | 'not_linked'
    | 'no_image'
    | 'shop_error'
    | 'no_account'
    | 'social_error';

export interface ContentPackItem {
    key: string;
    kind: ContentPackItemKind;
    status: ContentPackItemStatus;
    assetUuid?: string;
    imageUrl?: string;
    videoUrl?: string;
    caption?: string;
    hashtags?: string[];
    forAssetKey?: string;
    angle?: string;
    creditsCost?: number;
    approval: ContentPackApproval;
    approvedAt?: string;
    pushed?: {
        shop?: { status: 'done' | 'failed'; at: string; externalId?: string; error?: string; code?: ContentPackPushCode; platform?: string };
        social?: { postUuid: string; at: string; status?: 'scheduled' | 'draft'; scheduledAt?: string; platform?: string };
        socialPending?: { code: ContentPackPushCode; at: string; error?: string };
    };
    error?: string;
}

export interface ContentPack {
    uuid: string;
    productUuid: string;
    productId?: string;
    productTitle?: string;
    productThumbnailUrl?: string;
    status: ContentPackStatus;
    trigger: 'auto' | 'manual';
    items: ContentPackItem[];
    creditsEstimated: number;
    creditsHeld: number;
    creditsSpent: number;
    lang?: string;
    createdAt: string;
    updatedAt: string;
    /** Items made and not yet decided. */
    waiting: number;
}

export interface ContentPackList {
    items: ContentPack[];
    total: number;
    page: number;
    limit: number;
    counts: Record<ContentPackFilter, number>;
}

export interface PackEstimate {
    kinds: ContentPackItemKind[];
    posts: number;
    perKind: Record<ContentPackItemKind, number>;
    perPack: number;
    packs: number;
    total: number;
    balance?: number | null;
    enough?: boolean | null;
}

export interface ContentPackSettings {
    auto: boolean;
    kinds: ContentPackItemKind[];
    monthlyCreditCap: number;
    postsPerPack: number;
    windowDays: number;
    pushToShop: boolean;
    scheduleSocial: boolean;
}

export interface ContentPackSettingsView {
    settings: ContentPackSettings;
    usage: { since: string; credits: number; packs: number };
    estimate: PackEstimate;
    balance: number | null;
    shop: { connected: boolean; platform?: string; canPublish: boolean; code?: ContentPackPushCode };
    social: { uuid: string; platform: string; accountName?: string } | null;
}

export interface PackCandidate {
    uuid: string;
    id?: string;
    name: string;
    sku?: string;
    type?: string;
    productType?: string;
    thumbnailUrl?: string;
    createdAt?: string;
    canPack: boolean;
    lastPack: { uuid: string; status: ContentPackStatus; createdAt: string } | null;
}

const BASE = '/services/pms/content-packs';

export const useContentPacksApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();
    const send = useCallback(
        async <T,>(method: ApiMethod, path: string, body?: unknown): Promise<T> => (await sendAuthGuardedRequest(method, `${BASE}${path}`, body)) as T,
        [sendAuthGuardedRequest],
    );

    return useMemo(
        () => ({
            list: (query: { status?: ContentPackFilter; productUuid?: string; page?: number; limit?: number } = {}) => {
                const q = new URLSearchParams();
                Object.entries(query).forEach(([k, v]) => v !== undefined && v !== '' && q.set(k, String(v)));
                return send<ContentPackList>(ApiMethod.GET, q.toString() ? `?${q}` : '');
            },
            get: (uuid: string) => send<ContentPack>(ApiMethod.GET, `/${uuid}`),
            create: (body: { productUuids: string[]; kinds?: ContentPackItemKind[]; postsPerPack?: number }) =>
                send<{ packs: ContentPack[]; estimate: PackEstimate }>(ApiMethod.POST, '', body),
            estimate: (body: { productUuids?: string[]; kinds?: ContentPackItemKind[]; postsPerPack?: number }) =>
                send<PackEstimate>(ApiMethod.POST, '/estimate', body),
            candidates: (search?: string, limit = 30) => {
                const q = new URLSearchParams({ limit: String(limit) });
                if (search) q.set('search', search);
                return send<PackCandidate[]>(ApiMethod.GET, `/candidates?${q}`);
            },
            settings: () => send<ContentPackSettingsView>(ApiMethod.GET, '/settings'),
            updateSettings: (patch: Partial<ContentPackSettings>) => send<ContentPackSettingsView>(ApiMethod.PUT, '/settings', patch),
            approve: (uuid: string, key: string) => send<ContentPack>(ApiMethod.POST, `/${uuid}/items/${encodeURIComponent(key)}/approve`, {}),
            reject: (uuid: string, key: string) => send<ContentPack>(ApiMethod.POST, `/${uuid}/items/${encodeURIComponent(key)}/reject`, {}),
            regenerate: (uuid: string, key: string) => send<ContentPack>(ApiMethod.POST, `/${uuid}/items/${encodeURIComponent(key)}/regenerate`, {}),
            retryPush: (uuid: string, key: string) => send<ContentPack>(ApiMethod.POST, `/${uuid}/items/${encodeURIComponent(key)}/push`, {}),
            approveAll: (uuid: string) => send<ContentPack>(ApiMethod.POST, `/${uuid}/approve-all`, {}),
        }),
        [send],
    );
};
